import React from "react";
import { Smartphone, Apple, Download, ExternalLink, Info } from "lucide-react";
import { downloadApps } from "../data/apps";
import { LogoMark } from "./Logo";

const GooglePlayIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#00D4FF" d="M3.6 2.3 13.8 12 3.6 21.7c-.4-.2-.6-.6-.6-1.1V3.4c0-.5.2-.9.6-1.1Z" />
    <path fill="#3BE38A" d="m17.1 8.7-3.3 3.3L3.6 2.3c.2-.1.6-.1.9.1l12.6 6.3Z" />
    <path fill="#1E5BFF" d="m17.1 15.3-12.6 6.3c-.3.2-.7.2-.9.1L13.8 12l3.3 3.3Z" />
    <path fill="#FFC83D" d="m20.4 13.4-3.3 1.9-3.3-3.3 3.3-3.3 3.3 1.9c1 .6 1 2.2 0 2.8Z" />
  </svg>
);

const WindowsStoreIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#00D4FF" d="M3 5.1 10.2 4v7.3H3V5.1Z" />
    <path fill="#1E5BFF" d="M11.2 3.9 21 2.5v8.8h-9.8V3.9Z" />
    <path fill="#3BE38A" d="M3 12.3h7.2v7.3L3 18.6v-6.3Z" />
    <path fill="#00D4FF" d="M11.2 12.3H21v8.9l-9.8-1.4v-7.5Z" />
  </svg>
);

const ICONS = { android: Smartphone, googleplay: GooglePlayIcon, windows: WindowsStoreIcon, ios: Apple };

const NEW_TAB_ATTRS = { target: "_blank", rel: "noopener noreferrer" };

// Same-origin files get a download attr; external pages open in a new tab; external files are plain links.
const getLinkAttrs = (app) => {
  if (app.fileName) return { download: app.fileName };
  if (app.newTab) return NEW_TAB_ATTRS;
  return {};
};

const DownloadCard = ({ app }) => {
  const Icon = ICONS[app.id];
  const linkAttrs = getLinkAttrs(app);
  return (
    <div
      data-testid={`download-card-${app.id}`}
      className={`relative overflow-hidden rounded-3xl border p-6 md:p-7 flex flex-col bg-gradient-to-br from-ink-2 via-surface to-ink transition hover:-translate-y-1 ${
        app.featured ? "border-brand-2/50 shadow-2xl shadow-brand-2/15" : "border-white/10 hover:border-white/25"
      }`}
    >
      {app.featured && <div className="absolute inset-x-0 top-0 h-1 bg-brand-gradient" />}
      <div className="flex items-center gap-4">
        {app.featured ? (
          <LogoMark className="w-14 h-14" testId="download-android-icon" />
        ) : (
          <span className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
            <Icon className="w-7 h-7 text-brand-2" />
          </span>
        )}
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-brand-2 font-bold">{app.platform}</p>
          <h3 className="text-xl font-extrabold leading-tight">{app.name}</h3>
          <p className="text-xs text-silver/60">{app.subtitle}</p>
        </div>
      </div>
      <p className="mt-4 text-sm text-silver/75 leading-relaxed">{app.description}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {app.devices.map((d) => (
          <span key={d} className="px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-[11px] text-white/80 font-semibold">{d}</span>
        ))}
      </div>
      <p className="mt-4 flex gap-2 text-xs text-silver/70 leading-relaxed flex-1">
        <Info className="w-4 h-4 text-brand-3 shrink-0 mt-0.5" /> {app.hint}
      </p>
      <a
        href={app.url}
        {...linkAttrs}
        data-testid={`download-${app.id}`}
        className={`mt-6 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl font-bold transition ${
          app.featured ? "bg-brand-gradient text-white shadow-xl shadow-brand-2/30 hover:brightness-110" : "bg-white/8 hover:bg-white/15 border border-white/15 text-white"
        }`}
      >
        {app.newTab ? <ExternalLink className="w-4 h-4" /> : <Download className="w-4 h-4" />} {app.cta}
      </a>
    </div>
  );
};

export default function DownloadCards() {
  return (
    <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
      {downloadApps.map((a) => <DownloadCard key={a.id} app={a} />)}
    </div>
  );
}
